import type { QuickReportRow, QuickReportGroup, ReportPeriod } from "./types";

export function fmtMoney(n: number): string {
  const neg = n < 0;
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return neg ? `-$${formatted}` : `$${formatted}`;
}

export function fmtDateDMY(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function periodToDateRange(period: ReportPeriod, today = new Date()): { from: string; to: string } {
  const to = new Date(today);
  const from = new Date(today);
  switch (period) {
    case "last-7-days":
      from.setDate(from.getDate() - 7);
      break;
    case "last-30-days":
      from.setDate(from.getDate() - 30);
      break;
    case "this-month":
      from.setDate(1);
      break;
    case "this-quarter": {
      const q = Math.floor(today.getMonth() / 3);
      from.setMonth(q * 3);
      from.setDate(1);
      break;
    }
    case "since-90-days-ago":
      from.setDate(from.getDate() - 90);
      break;
    case "ytd":
      from.setMonth(0);
      from.setDate(1);
      break;
    case "all-dates":
      from.setFullYear(1970);
      from.setMonth(0);
      from.setDate(1);
      break;
    case "custom":
      break;
  }
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

export function computeRunningBalance(rows: QuickReportRow[]): QuickReportRow[] {
  let running = 0;
  return rows
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((r) => {
      running += r.amount;
      return { ...r, balance: running };
    });
}

export function groupByDistributionAccount(rows: QuickReportRow[]): QuickReportGroup[] {
  const map = new Map<string, QuickReportRow[]>();
  for (const r of rows) {
    const key = r.distributionAccount;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  const groups: QuickReportGroup[] = [];
  for (const [key, grpRows] of map.entries()) {
    const subtotal = grpRows.reduce((s, r) => s + r.amount, 0);
    const endBalance = grpRows[grpRows.length - 1]?.balance ?? 0;
    groups.push({ key, label: key, rows: grpRows, subtotal, endBalance });
  }
  return groups;
}

export function filterByDateRange(rows: QuickReportRow[], from: string, to: string): QuickReportRow[] {
  return rows.filter((r) => r.date >= from && r.date <= to);
}